import React, { useMemo } from "react";
import { ImageBanner } from "components/imageBanner/imageBanner";
import * as CONTACT_INFO from "content/pages/contact.json";
import * as CONTACT_DETAILS from "content/site/contactDetails.json";
import { marked } from "marked";
import {
  faPhone,
  faEnvelope,
  faMapMarker,
} from "@fortawesome/fontawesome-free-solid";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SocialMedia } from "components/socialMedia/socialMedia";

export const Contact: React.FC = () => {
  const data = CONTACT_INFO;
  const contactDetails = CONTACT_DETAILS;

  // Parse markdown content
  const markdownParsed = useMemo(() => {
    return marked.parse(data.body) as string;
  }, [data.body]);

  return (
    <div
      className="relative flex min-h-screen flex-col bg-white text-black"
      style={{
        backgroundImage:
          "radial-gradient(#24557e24 1px, transparent 1px), linear-gradient(to bottom, #ffffff, #f3f3f1)",
        backgroundSize: "20px 20px, 100% 100%",
      }}
    >
      {/* Hero Banner */}
      <ImageBanner src={data.thumbnail} title={data.title} />

      {/* Main Content Grid Area */}
      <div className="mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-start">
          {/* ================= LEFT COLUMN: INFO & DETAILS ================= */}
          <div className="flex flex-col space-y-12 lg:col-span-5">
            {/* Structural Intro Text (Markdown) */}
            <div
              className="prose max-w-none text-neutral-700 [&>p]:mb-4 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:text-neutral-600"
              dangerouslySetInnerHTML={{ __html: markdownParsed }}
            />

            {/* Technical Detail Box with Corner Crosshairs */}
            <div className="relative border border-[#d8d8d8] bg-whiteColor p-8">
              {/* Corner Accents */}
              {/* top-left */}
              <div className="absolute -top-2 -left-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 mr-2 mb-2 bg-blackColor rounded-full" />
              </div>
              {/* top-right */}
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 ml-2 mb-2 bg-blackColor rounded-full" />
              </div>
              {/* bottom-left */}
              <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 mt-2 mr-2 bg-blackColor rounded-full" />
              </div>
              {/* bottom-right */}
              <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 ml-2 mt-2 bg-blackColor rounded-full" />
              </div>

              <h4 className="text-sm font-bold uppercase tracking-widest text-blackColor mb-8 pb-3 border-b border-[#d8d8d8]">
                Contact Details
              </h4>

              <div className="space-y-5">
                {/* Phone Row */}
                <div className="flex items-center gap-4 group">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#d8d8d8] bg-neutral-50 text-neutral-800 transition-colors group-hover:bg-neutral-100">
                    <FontAwesomeIcon
                      icon={faPhone as IconProp}
                      className="h-3.5 w-3.5"
                    />
                  </div>
                  <h6 className="text-sm font-medium tracking-wide text-neutral-800">
                    {contactDetails.phoneNumber}
                  </h6>
                </div>

                {/* Email Row */}
                <div className="flex items-center gap-4 group">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#d8d8d8] bg-neutral-50 text-neutral-800 transition-colors group-hover:bg-neutral-100">
                    <FontAwesomeIcon
                      icon={faEnvelope as IconProp}
                      className="h-3.5 w-3.5"
                    />
                  </div>
                  <h6 className="text-sm font-medium tracking-wide text-neutral-800 break-all">
                    {contactDetails.email}
                  </h6>
                </div>

                {/* Address Row */}
                <div className="flex items-start gap-4 group">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#d8d8d8] bg-neutral-50 text-neutral-800 transition-colors group-hover:bg-neutral-100 mt-0.5">
                    <FontAwesomeIcon
                      icon={faMapMarker as IconProp}
                      className="h-3.5 w-3.5"
                    />
                  </div>
                  <h6 className="text-sm font-medium tracking-wide text-neutral-700 leading-relaxed">
                    {contactDetails.address}
                  </h6>
                </div>
              </div>

              {/* Social Media Block integration */}
              <div className="mt-8 pt-6 border-t border-[#d8d8d8]">
                <SocialMedia />
              </div>
            </div>
          </div>

          {/* ================= RIGHT COLUMN: INTERACTIVE FORM ================= */}
          <div className="lg:col-span-7">
            <div className="relative border border-[#d8d8d8] bg-whiteColor p-8 md:p-12">
              {/* Corner Accents */}
              {/* top-left */}
              <div className="absolute -top-2 -left-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 mr-2 mb-2 bg-blackColor rounded-full" />
              </div>
              {/* top-right */}
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 ml-2 mb-2 bg-blackColor rounded-full" />
              </div>
              {/* bottom-left */}
              <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 mt-2 mr-2 bg-blackColor rounded-full" />
              </div>
              {/* bottom-right */}
              <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-whiteColor flex items-center justify-center pointer-events-none">
                <div className="w-1 h-1 ml-2 mt-2 bg-blackColor rounded-full" />
              </div>

              {/* Form Heading Header */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold tracking-tight text-blackColor uppercase">
                  {data.contactFormHeader}
                </h2>
                <p className="mt-3 text-sm text-neutral-500 leading-relaxed">
                  {data.contactFormDescription}
                </p>
              </div>

              <form
                className="flex flex-col gap-6"
                name="contact-clifton-technology-form"
                action="/"
                method="POST"
                data-netlify="true"
                onSubmit={() => {
                  window.alert(
                    "Your message was sent. Thank you for contacting Clifton Technology."
                  );
                }}
              >
                <input
                  type="hidden"
                  name="form-name"
                  value="contact-clifton-technology-form"
                />
                <div className="hidden">
                  <label>
                    Fill this out if you're human:
                    <input title="text" type="text" name="beep-boop-field" />
                  </label>
                </div>

                {/* Row 1 */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      title="name"
                      type="text"
                      name="name"
                      required
                      className="rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor"
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      title="email"
                      type="email"
                      name="email"
                      required
                      className="rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                      Company <span className="text-red-500">*</span>
                    </label>
                    <input
                      title="company"
                      type="text"
                      name="company"
                      required
                      className="rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor"
                      placeholder="Your company"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <input
                      title="country"
                      type="text"
                      name="country"
                      required
                      className="rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor"
                      placeholder="United Kingdom"
                    />
                  </div>
                </div>

                {/* Website input */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                    Current website{" "}
                    <span className="text-neutral-400 font-normal lowercase">
                      (optional)
                    </span>
                  </label>
                  <input
                    title="website"
                    type="url"
                    name="website"
                    className="rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                {/* Message block */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="min-h-[160px] rounded-none border border-[#d8d8d8] bg-white px-4 py-3 text-sm text-black outline-none transition-colors placeholder:text-neutral-400 focus:border-blackColor resize-y"
                    title="message"
                    name="message"
                    rows={5}
                    required
                    placeholder="Tell me about your project..."
                  />
                </div>

                {/* Action Submit Button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-none bg-blackColor text-whiteColor px-10 py-4 text-xs font-bold uppercase tracking-widest transition-colors hover:bg-neutral-800 active:bg-black"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
